// Supabase data layer — load + per-entity upsert/delete.
// Frontend stays the same shape; this layer translates and persists.

import { supabase } from "./supabaseClient";
import {
  clientToRow, rowToClient,
  offerToRow, rowToOffer,
  paymentToRow, rowToPayment,
  teamToRow, rowToTeam,
  vacationToRow, rowToVacation,
  contractorPaymentToRow, rowToContractorPayment,
  toolToRow, rowToTool,
  toolTransactionToRow, rowToToolTransaction,
  oneOffToRow, rowToOneOff,
  fxRateToRow, rowToFxRate,
  dividendToRow, rowToDividend,
  partnerRuleToRow, rowToPartnerRule,
} from "./dbMappers";

const logError = (label, error) => {
  if (error) console.error(`db.${label} error`, error);
};

export async function loadAll(userId) {
  const [
    clients, offers, payments, team, vacations, contractorPayments, tools, toolTransactions, oneOff, fx, partnerRules, dividends, settingsRow,
  ] = await Promise.all([
    supabase.from("clients").select("*").eq("owner_id", userId),
    supabase.from("offers").select("*").eq("owner_id", userId),
    supabase.from("payments").select("*").eq("owner_id", userId),
    supabase.from("team").select("*").eq("owner_id", userId),
    supabase.from("vacations").select("*").eq("owner_id", userId),
    supabase.from("contractor_payments").select("*").eq("owner_id", userId),
    supabase.from("tools").select("*").eq("owner_id", userId),
    supabase.from("tool_transactions").select("*").eq("owner_id", userId),
    supabase.from("one_off_payments").select("*").eq("owner_id", userId),
    supabase.from("fx_rates").select("*").eq("owner_id", userId),
    supabase.from("partner_rules").select("*").eq("owner_id", userId),
    supabase.from("dividends").select("*").eq("owner_id", userId),
    supabase.from("settings").select("data").eq("owner_id", userId).maybeSingle(),
  ]);

  [clients, offers, payments, team, vacations, contractorPayments, tools, toolTransactions, oneOff, fx, partnerRules, dividends, settingsRow].forEach((res, i) => {
    const labels = ["clients","offers","payments","team","vacations","contractor_payments","tools","tool_transactions","one_off","fx","partner_rules","dividends","settings"];
    logError(`load.${labels[i]}`, res.error);
  });

  return {
    clients: (clients.data || []).map(rowToClient),
    offers: (offers.data || []).map(rowToOffer),
    payments: (payments.data || []).map(rowToPayment),
    team: (team.data || []).map(rowToTeam),
    vacations: (vacations.data || []).map(rowToVacation),
    contractorPayments: (contractorPayments.data || []).map(rowToContractorPayment),
    tools: (tools.data || []).map(rowToTool),
    toolTransactions: (toolTransactions.data || []).map(rowToToolTransaction),
    oneOffPayments: (oneOff.data || []).map(rowToOneOff),
    fxRates: (fx.data || []).map(rowToFxRate),
    partnerRules: (partnerRules.data || []).map(rowToPartnerRule),
    dividends: (dividends.data || []).map(rowToDividend),
    settings: settingsRow.data?.data || null, // null = use defaults
  };
}

// ── Clients ─────────────────────────────────────────────────────────────────
export const upsertClient = (c, ownerId) =>
  supabase.from("clients").upsert(clientToRow(c, ownerId)).then((r) => { logError("upsertClient", r.error); return r; });
export const deleteClient = (id) =>
  supabase.from("clients").delete().eq("id", id).then((r) => { logError("deleteClient", r.error); return r; });
export const deleteClientsBulk = (ids) =>
  supabase.from("clients").delete().in("id", ids).then((r) => { logError("deleteClientsBulk", r.error); return r; });

// ── Offers ──────────────────────────────────────────────────────────────────
export const upsertOffer = (o, ownerId) =>
  supabase.from("offers").upsert(offerToRow(o, ownerId)).then((r) => { logError("upsertOffer", r.error); return r; });
export const deleteOffer = (id) =>
  supabase.from("offers").delete().eq("id", id).then((r) => { logError("deleteOffer", r.error); return r; });

// ── Payments ────────────────────────────────────────────────────────────────
export const upsertPayment = (p, ownerId) =>
  supabase.from("payments").upsert(paymentToRow(p, ownerId)).then((r) => { logError("upsertPayment", r.error); return r; });
export const upsertPaymentsBulk = (rows, ownerId) =>
  supabase.from("payments").upsert(rows.map((p) => paymentToRow(p, ownerId))).then((r) => { logError("upsertPaymentsBulk", r.error); return r; });
export const deletePayment = (id) =>
  supabase.from("payments").delete().eq("id", id).then((r) => { logError("deletePayment", r.error); return r; });
export const deletePaymentsBulk = (ids) =>
  supabase.from("payments").delete().in("id", ids).then((r) => { logError("deletePaymentsBulk", r.error); return r; });

// ── Team ────────────────────────────────────────────────────────────────────
export const upsertTeamMember = (t, ownerId) =>
  supabase.from("team").upsert(teamToRow(t, ownerId)).then((r) => { logError("upsertTeamMember", r.error); return r; });
export const deleteTeamMember = (id) =>
  supabase.from("team").delete().eq("id", id).then((r) => { logError("deleteTeamMember", r.error); return r; });

// ── Vacations ───────────────────────────────────────────────────────────────
export const upsertVacation = (v, ownerId) =>
  supabase.from("vacations").upsert(vacationToRow(v, ownerId)).then((r) => { logError("upsertVacation", r.error); return r; });
export const deleteVacation = (id) =>
  supabase.from("vacations").delete().eq("id", id).then((r) => { logError("deleteVacation", r.error); return r; });

// ── Contractor payments ─────────────────────────────────────────────────────
export const upsertContractorPayment = (p, ownerId) =>
  supabase.from("contractor_payments").upsert(contractorPaymentToRow(p, ownerId)).then((r) => { logError("upsertContractorPayment", r.error); return r; });
export const deleteContractorPayment = (id) =>
  supabase.from("contractor_payments").delete().eq("id", id).then((r) => { logError("deleteContractorPayment", r.error); return r; });

// ── Tools ───────────────────────────────────────────────────────────────────
export const upsertTool = (t, ownerId) =>
  supabase.from("tools").upsert(toolToRow(t, ownerId)).then((r) => { logError("upsertTool", r.error); return r; });
export const deleteTool = (id) =>
  supabase.from("tools").delete().eq("id", id).then((r) => { logError("deleteTool", r.error); return r; });

// ── Tool transactions (variable tools) ──────────────────────────────────────
export const upsertToolTransaction = (t, ownerId) =>
  supabase.from("tool_transactions").upsert(toolTransactionToRow(t, ownerId)).then((r) => { logError("upsertToolTransaction", r.error); return r; });
export const deleteToolTransaction = (id) =>
  supabase.from("tool_transactions").delete().eq("id", id).then((r) => { logError("deleteToolTransaction", r.error); return r; });

// ── One-off payments ────────────────────────────────────────────────────────
export const upsertOneOffPayment = (p, ownerId) =>
  supabase.from("one_off_payments").upsert(oneOffToRow(p, ownerId)).then((r) => { logError("upsertOneOff", r.error); return r; });
export const deleteOneOffPayment = (id) =>
  supabase.from("one_off_payments").delete().eq("id", id).then((r) => { logError("deleteOneOff", r.error); return r; });

// ── FX rates ────────────────────────────────────────────────────────────────
export const upsertFxRate = (rate, ownerId) =>
  supabase.from("fx_rates").upsert(fxRateToRow(rate, ownerId), { onConflict: "owner_id,month" })
    .then((r) => { logError("upsertFxRate", r.error); return r; });
export const deleteFxRate = (month, ownerId) =>
  supabase.from("fx_rates").delete().eq("owner_id", ownerId).eq("month", month)
    .then((r) => { logError("deleteFxRate", r.error); return r; });

// ── Dividends (money paid out to Ahmed / partners) ─────────────────────────
export const upsertDividend = (d, ownerId) =>
  supabase.from("dividends").upsert(dividendToRow(d, ownerId)).then((r) => { logError("upsertDividend", r.error); return r; });
export const deleteDividend = (id) =>
  supabase.from("dividends").delete().eq("id", id).then((r) => { logError("deleteDividend", r.error); return r; });

// ── Partner rules (revenue-share / profit-share cuts) ──────────────────────
export const upsertPartnerRule = (r, ownerId) =>
  supabase.from("partner_rules").upsert(partnerRuleToRow(r, ownerId)).then((res) => { logError("upsertPartnerRule", res.error); return res; });
export const deletePartnerRule = (id) =>
  supabase.from("partner_rules").delete().eq("id", id).then((r) => { logError("deletePartnerRule", r.error); return r; });

// ── Settings ────────────────────────────────────────────────────────────────
export const upsertSettings = (data, ownerId) =>
  supabase.from("settings").upsert({ owner_id: ownerId, data, updated_at: new Date().toISOString() })
    .then((r) => { logError("upsertSettings", r.error); return r; });
